import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '@api/v1/client'
import emailService from '@api/v1/emailService'
import useDocumentTitle from '@hooks/useDocumentTitle'

const OPERATIONS = [
  { id: 'email-invoices', label: 'Email Invoices', description: 'Send invoices to clients via email in bulk', category: 'Billing' },
  { id: 'archive-matters', label: 'Archive Matters', description: 'Archive multiple matters at once', category: 'Matters' },
  { id: 'archive-contacts', label: 'Archive Contacts', description: 'Archive multiple contacts at once', category: 'Contacts' },
]

const BulkOperations = () => {
  useDocumentTitle('Bulk Operations')
  const [selectedOp, setSelectedOp] = useState(null)

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Bulk Operations</h1>
        <p className="text-sm text-gray-500 mt-1">Perform batch actions across multiple records</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {OPERATIONS.map((op) => (
          <button
            key={op.id}
            onClick={() => setSelectedOp(op.id)}
            className={`text-left px-5 py-4 rounded-xl border transition-all ${
              selectedOp === op.id
                ? 'border-cb-accent bg-cb-light shadow-sm'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            <p className="text-xs text-gray-400 uppercase font-semibold">{op.category}</p>
            <p className="text-sm font-medium text-gray-900 mt-1">{op.label}</p>
            <p className="text-xs text-gray-500 mt-1">{op.description}</p>
          </button>
        ))}
      </div>

      {selectedOp === 'email-invoices' && <BulkEmailInvoices />}
      {selectedOp === 'archive-matters' && <BulkArchiveInfo type="matters" />}
      {selectedOp === 'archive-contacts' && <BulkArchiveInfo type="contacts" />}

      {!selectedOp && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-sm text-gray-500">Select an operation above to get started</p>
        </div>
      )}
    </div>
  )
}

/*
 * Spec 183 (CC-54197) — what this page may say about a bulk invoice email.
 *
 * It used to toast "Bulk email initiated" on any 2xx, and the route was a verbatim relay
 * that wrapped CB's refusals in a 200. cb-api now builds CB's request itself (sender bound
 * to the signed-in user), refuses before CB with a sentence saying why, and answers a 200
 * only as `{ status: 'started' }` — CB starting its background run, which is NOT proof that
 * any invoice went out. So: the server's own sentence for every refusal, a fixed sentence
 * for an answer we cannot read (never axios's "Request failed with status code 500"), and
 * both in live regions that are in the page before the send, not a toast.
 */
const STARTED_TITLE = 'Centerbase started the bulk invoice email.'
const STARTED_NOTE = 'It runs in the background. Centerbase doesn’t confirm delivery or say which invoices were emailed.'
const UNKNOWN_OUTCOME = 'We couldn’t tell whether the bulk invoice email started. Check the invoices in Centerbase before trying again.'

function outcomeOfAnswer(res) {
  return res?.data?.status === 'started' ? { kind: 'started' } : { kind: 'problem', message: UNKNOWN_OUTCOME }
}

function outcomeOfFailure(err) {
  const said = err?.response?.data?.error
  if (err?.response && typeof said === 'string' && said.trim()) return { kind: 'problem', message: said }
  return { kind: 'problem', message: UNKNOWN_OUTCOME }
}

const BulkEmailInvoices = () => {
  const [templateId, setTemplateId] = useState('')
  // The last attempt's outcome. It stays until the next attempt — never on a timer.
  const [outcome, setOutcome] = useState(null)

  const bulkMutation = useMutation({
    mutationFn: (data) => emailService.bulkSend(data),
    // Cleared first, so a repeated outcome is announced again.
    onMutate: () => setOutcome(null),
    onSuccess: (res) => setOutcome(outcomeOfAnswer(res)),
    onError: (err) => setOutcome(outcomeOfFailure(err)),
  })

  const started = outcome?.kind === 'started'
  const problem = outcome?.kind === 'problem'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Email Invoices in Bulk</h2>
      <p className="text-sm text-gray-500 mb-4">
        Send outstanding invoices to clients via email. Select a template and click send.
      </p>
      <div className="flex items-end gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Template ID</label>
          <input
            type="text"
            aria-label="Email template ID"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            placeholder="Enter template ID"
            className="w-full px-3 py-2 border border-rc-control rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
          />
        </div>
        {/* No parseInt: "12abc" is not template 12. A non-number reaches cb-api as null and is refused there. */}
        <button
          type="button"
          onClick={() => bulkMutation.mutate({ templateId: Number(templateId.trim()) })}
          disabled={!templateId.trim() || bulkMutation.isPending}
          className="btn-cb-primary"
        >
          {bulkMutation.isPending ? 'Sending...' : 'Send Bulk Email'}
        </button>
      </div>
      {/* Both regions are always mounted (empty until an answer): a live region inserted
          together with its text is not reliably announced. */}
      <div className="mt-4">
        <div
          role="status"
          aria-live="polite"
          data-testid="bulk-email-status"
          className={started ? 'rounded-lg border border-gray-200 bg-white p-3' : ''}
        >
          {started && (
            <>
              <p className="text-sm font-semibold text-gray-900">{STARTED_TITLE}</p>
              <p className="mt-1 text-[12.5px] text-gray-600">{STARTED_NOTE}</p>
            </>
          )}
        </div>
        <div
          role="alert"
          data-testid="bulk-email-alert"
          className={problem ? 'rounded-lg border border-amber-200 bg-amber-50 p-3' : ''}
        >
          {problem && <p className="text-sm text-amber-900">{outcome.message}</p>}
        </div>
      </div>
    </div>
  )
}

const BulkArchiveInfo = ({ type }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <h2 className="text-base font-semibold text-gray-900 mb-2">Bulk Archive {type === 'matters' ? 'Matters' : 'Contacts'}</h2>
    <p className="text-sm text-gray-500">
      To bulk archive, go to the <a href={`/${type}`} className="text-cb-accent hover:text-cb-navy font-medium">{type === 'matters' ? 'Matters' : 'Contacts'}</a> page,
      select multiple rows using the checkboxes, then click "Archive Selected" in the action bar.
    </p>
  </div>
)

export default BulkOperations
